import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, Loader, Search, ShieldCheck, X } from 'lucide-react';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { kanbanApiService } from '../../services/kanbanApi';
import { useUsername, usernameCacheService } from '../../services/usernameCache';
import type { KanbanBoardAccessOptions } from '../../types/kanban';

function StaffName({ uuid, name }: { uuid: string; name?: string }) {
  const cachedName = useUsername(uuid);
  return <span className="truncate">{name || cachedName || uuid}</span>;
}

interface BoardAccessModalProps {
  boardId: string;
  boardTitle: string;
  onClose: () => void;
  onSaved: () => void;
}

export function BoardAccessModal({ boardId, boardTitle, onClose, onSaved }: BoardAccessModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const [options, setOptions] = useState<KanbanBoardAccessOptions>({ staff_teams: [], staff_users: [] });
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [teamSearch, setTeamSearch] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [canManage, setCanManage] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadVersion, setLoadVersion] = useState(0);
  const [namesVersion, setNamesVersion] = useState(0);
  const closeWhenIdle = useCallback(() => { if (!saving) onClose(); }, [saving, onClose]);
  useOnClickOutside(modalRef, closeWhenIdle);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      kanbanApiService.getBoardAccess(boardId),
      kanbanApiService.getBoardAccessOptions(boardId),
    ]).then(([access, roster]) => {
      if (cancelled) return;
      setOptions(roster);
      setSelectedTeams(access.staff_teams);
      setSelectedUsers(access.staff_users);
      setCanManage(access.can_manage);
      void usernameCacheService.ensureUsernames(roster.staff_users.filter((user) => !user.username).map((user) => user.minecraft_uuid))
        .then(() => { if (!cancelled) setNamesVersion((version) => version + 1); })
        .catch(() => { /* UUID search remains available when names cannot be resolved. */ });
    }).catch((err) => {
      if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load board access.');
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [boardId, loadVersion]);

  const filteredTeams = useMemo(() => {
    const query = teamSearch.trim().toLowerCase();
    return options.staff_teams.filter((team) =>
      [team.display_name, team.name, team.domain, team.id].some((value) => value.toLowerCase().includes(query)));
  }, [options, teamSearch]);

  const filteredUsers = useMemo(() => {
    const query = userSearch.trim().toLowerCase();
    const compactQuery = query.replace(/-/g, '');
    return options.staff_users.filter((user) => {
      const name = user.username || usernameCacheService.getName(user.minecraft_uuid) || '';
      return name.toLowerCase().includes(query) || user.minecraft_uuid.toLowerCase().includes(query) ||
        user.minecraft_uuid.replace(/-/g, '').toLowerCase().includes(compactQuery);
    });
  }, [options, userSearch, namesVersion]);

  const toggle = (values: string[], value: string) => values.includes(value)
    ? values.filter((entry) => entry !== value) : [...values, value];

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canManage || loading || saving) return;
    setSaving(true);
    setError(null);
    try {
      await kanbanApiService.setBoardAccess(boardId, { staff_teams: selectedTeams, staff_users: selectedUsers });
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save board access.');
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="modal-overlay z-[9999]">
      <div ref={modalRef} className="modal-panel max-w-2xl" role="dialog" aria-modal="true" aria-labelledby="board-access-title">
        <button type="button" className="modal-close" onClick={onClose} disabled={saving} aria-label="Close">
          <X className="w-5 h-5" />
        </button>
        <div className="modal-header flex-col items-start gap-1">
          <h2 id="board-access-title" className="modal-title flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-accent-400" /> Board access
          </h2>
          <p className="text-sm text-glass-400 mt-2">Grant staff teams and individual staff members permission to view “{boardTitle}”.</p>
          <p className="text-xs text-glass-500 mt-1">These grants provide view access. Existing team editing and management permissions still apply.</p>
        </div>
        <form onSubmit={save} className="space-y-4">
          {error && <div className="alert alert-danger" role="alert"><AlertCircle /><span>{error}</span></div>}
          {loading ? (
            <div className="flex items-center gap-2 py-6 text-glass-400" role="status"><Loader className="w-4 h-4 animate-spin" /> Loading staff and access…</div>
          ) : !canManage ? (
            <div className="text-sm text-glass-400">Board creators and managers can change access.
              {error && <button type="button" className="glass-button-sm ml-2" onClick={() => setLoadVersion((version) => version + 1)}>Retry</button>}
            </div>
          ) : (
            <>
              <section aria-labelledby="board-access-teams">
                <h3 id="board-access-teams" className="text-sm font-medium text-white mb-2">Staff teams ({selectedTeams.length} selected)</h3>
                {selectedTeams.length > 0 && <div className="flex flex-wrap gap-1.5 mb-2">
                  {selectedTeams.map((id) => {
                    const team = options.staff_teams.find((entry) => entry.id === id);
                    return <button key={id} type="button" className="chip max-w-full" disabled={saving} onClick={() => setSelectedTeams((values) => values.filter((value) => value !== id))} aria-label={`Remove ${team?.display_name || id}`}>
                      <span className="truncate">{team ? `${team.display_name || team.name} · ${team.name}${team.domain ? ` · ${team.domain}` : ''}` : id}</span><X className="w-3 h-3 shrink-0" />
                    </button>;
                  })}
                </div>}
                <div className="relative mb-2"><Search className="absolute left-3 top-3 w-4 h-4 text-glass-400" /><input className="glass-input w-full pl-9" value={teamSearch} onChange={(event) => setTeamSearch(event.target.value)} placeholder="Search staff teams" aria-label="Search staff teams" disabled={saving} /></div>
                <div className="max-h-40 overflow-y-auto space-y-1 rounded-lg border border-glass-200/10 p-2">
                  {filteredTeams.map((team) => <label key={team.id} className="flex items-center gap-3 p-2 rounded hover:bg-glass-100/10 cursor-pointer">
                    <input type="checkbox" checked={selectedTeams.includes(team.id)} onChange={() => setSelectedTeams((values) => toggle(values, team.id))} disabled={saving} />
                    <span className="min-w-0"><span className="block text-sm text-white">{team.display_name || team.name}</span><span className="block text-xs text-glass-400">{team.name}{team.domain ? ` · ${team.domain}` : ''} · {team.id}</span></span>
                  </label>)}
                  {filteredTeams.length === 0 && <p className="text-xs text-glass-400 p-2">No matching staff teams.</p>}
                </div>
              </section>
              <section aria-labelledby="board-access-users">
                <h3 id="board-access-users" className="text-sm font-medium text-white mb-2">Staff users ({selectedUsers.length} selected)</h3>
                {selectedUsers.length > 0 && <div className="flex flex-wrap gap-1.5 mb-2">
                  {selectedUsers.map((uuid) => <button key={uuid} type="button" className="chip max-w-full" disabled={saving} onClick={() => setSelectedUsers((values) => values.filter((value) => value !== uuid))} aria-label={`Remove staff user ${uuid}`}>
                    <StaffName uuid={uuid} name={options.staff_users.find((user) => user.minecraft_uuid === uuid)?.username} /><X className="w-3 h-3 shrink-0" />
                  </button>)}
                </div>}
                <div className="relative mb-2"><Search className="absolute left-3 top-3 w-4 h-4 text-glass-400" /><input className="glass-input w-full pl-9" value={userSearch} onChange={(event) => setUserSearch(event.target.value)} placeholder="Search staff by username or UUID" aria-label="Search staff users" disabled={saving} /></div>
                <div className="max-h-48 overflow-y-auto space-y-1 rounded-lg border border-glass-200/10 p-2">
                  {filteredUsers.slice(0, 40).map((user) => <label key={user.minecraft_uuid} className="flex items-center gap-3 p-2 rounded hover:bg-glass-100/10 cursor-pointer">
                    <input type="checkbox" checked={selectedUsers.includes(user.minecraft_uuid)} onChange={() => setSelectedUsers((values) => toggle(values, user.minecraft_uuid))} disabled={saving} />
                    <span className="min-w-0 text-sm text-white"><StaffName uuid={user.minecraft_uuid} name={user.username} /><span className="block text-xs text-glass-400 truncate">{user.minecraft_uuid}</span></span>
                  </label>)}
                  {filteredUsers.length === 0 && <p className="text-xs text-glass-400 p-2">No matching staff users.</p>}
                  {filteredUsers.length > 40 && <p className="text-xs text-glass-400 p-2">Showing 40 of {filteredUsers.length} staff users. Search to narrow the list.</p>}
                </div>
              </section>
            </>
          )}
          <div className="modal-footer">
            <button type="button" onClick={onClose} className="glass-button-secondary" disabled={saving}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={loading || saving || !canManage}>{saving && <Loader className="w-4 h-4 animate-spin" />}Save access</button>
          </div>
        </form>
      </div>
    </div>, document.body,
  );
}
